import { getFunctions, httpsCallable } from 'firebase/functions';
import app from '../firebase/firebaseConfig';

/** Must match Cloud Functions `onCall({ region: 'us-central1', ... })`. */
const FUNCTIONS_REGION = 'us-central1';

function subscriptionQuotaFunctions() {
  return getFunctions(app, FUNCTIONS_REGION);
}

/** Keep in sync with `FREE_COOKBOOK_CREATIONS_PER_MONTH` in `functions/subscriptionTierLimits.ts`. */
const FREE_COOKBOOK_CREATIONS_PER_MONTH = 2;

/**
 * Snippet preflight fallback when `validateSnippetCreation` cannot be reached.
 * Returns metadata so callers can show `messageForSnippetQuotaCheckUnavailable` and fail closed
 * (aligned with cookbook preflight UX).
 */
function snippetCallableUnavailablePayload(kind, e) {
  const details =
    typeof e?.details === 'string'
      ? e.details
      : e?.details != null
        ? String(e.details)
        : null;
  console.warn(`[subscriptionQuota] ${kind} failed`, e?.code, e?.message);
  return {
    allowed: false,
    isPremium: false,
    quotaCheckUnavailable: true,
    limit: null,
    remaining: null,
    callableCode: e?.code ?? null,
    callableMessage: e?.message ?? null,
    callableDetails: details,
  };
}

/**
 * Cookbook preflight: fail closed if we cannot reach the server. Otherwise a broken deploy
 * (no `validateCookbookSave` / `enforceLibraryCookbookLimit`) would allow unlimited cookbooks.
 */
function cookbookCallableUnavailablePayload(kind, e) {
  console.warn(`[subscriptionQuota] ${kind} failed`, e?.code, e?.message, e?.details);
  const details =
    typeof e?.details === 'string'
      ? e.details
      : e?.details != null
        ? String(e.details)
        : null;
  return {
    allowed: false,
    isPremium: false,
    quotaCheckUnavailable: true,
    limit: FREE_COOKBOOK_CREATIONS_PER_MONTH,
    remaining: null,
    createdThisMonth: null,
    callableCode: e?.code ?? null,
    callableMessage: e?.message ?? null,
    callableDetails: details,
  };
}

/**
 * User-facing copy when validateCookbookSave could not run (network, deploy, Cloud Run quota, etc.).
 * This is not the same as "you hit the monthly cookbook cap" (that comes back as allowed: false without quotaCheckUnavailable).
 */
export function messageForCookbookQuotaCheckUnavailable(quota) {
  if (!quota?.quotaCheckUnavailable) return '';
  const custom = (quota.userMessage || '').trim();
  if (custom.length > 0) {
    return custom;
  }
  const code = quota.callableCode || '';
  if (code === 'functions/internal' || code === 'internal') {
    const hint = (quota.callableDetails || '').trim();
    const tail =
      " Fix: run `firebase deploy --only functions:validateCookbookSave` (repo already sets invoker: 'public'), then Cloud Run → validatecookbooksave → Permissions: allUsers = Cloud Run Invoker. In DevTools → Network, confirm the callable is not 403.";
    if (hint.length > 0) {
      return `${hint}${tail}`;
    }
    return (
      'Cookbook limit check did not get a response from the server (often 403: Cloud Run Invoker missing, or a network block).'
      + tail
    );
  }
  if (code === 'functions/unavailable' || code === 'unavailable') {
    const m = (quota.callableMessage || '').trim();
    if (m.length > 0 && !/^internal$/i.test(m)) {
      return m;
    }
    return 'Could not reach the cookbook limit service. Check your connection and try again.';
  }
  if (code === 'functions/not-found' || code === 'not-found') {
    return 'Cookbook limit check is not available (function not deployed). Deploy validateCookbookSave or try again later.';
  }
  return 'Could not verify your cookbook limit. Check your connection and try again. If your backend is still deploying, wait until it finishes.';
}

/**
 * Same limit copy as FullPageCreator / deck editor manual snippet flows.
 *
 * @param {{ limit?: number | null }} quota
 */
export function messageSnippetCreationMonthlyLimitReached(quota) {
  const lim = quota?.limit != null ? quota.limit : '-';
  return (
    `You've reached your limit of ${lim} new snippets this month. Your quota resets on the 1st (UTC). Upgrade to Pro for unlimited snippets and AI.`
  );
}

/**
 * When validateSnippetCreation did not return (network, deploy, permissions).
 */
export function messageForSnippetQuotaCheckUnavailable(quota) {
  if (!quota?.quotaCheckUnavailable) return '';
  const custom = (quota.userMessage || '').trim();
  if (custom.length > 0) {
    return custom;
  }
  const code = quota.callableCode || '';
  if (code === 'functions/internal' || code === 'internal') {
    const hint = (quota.callableDetails || '').trim();
    const tail =
      ' Deploy `functions:validateSnippetCreation` and ensure the HTTPS callable invoker (e.g. Cloud Run “allUsers” = Invoker) is set. In DevTools → Network, confirm the callable is not 403.';
    if (hint.length > 0) {
      return `${hint}${tail}`;
    }
    return (
      'Snippet limit check did not get a response from the server (often 403: Cloud Run Invoker missing, or a network block).'
      + tail
    );
  }
  if (code === 'functions/unavailable' || code === 'unavailable') {
    const m = (quota.callableMessage || '').trim();
    if (m.length > 0 && !/^internal$/i.test(m)) {
      return m;
    }
    return 'Could not reach the snippet limit service. Check your connection and try again.';
  }
  if (code === 'functions/not-found' || code === 'not-found') {
    return 'Snippet limit check is not available (function not deployed). Deploy validateSnippetCreation or try again later.';
  }
  return 'Could not verify your snippet limit. Check your connection and try again. If your backend is still deploying, wait until it finishes.';
}

/**
 * @returns {Promise<{ allowed: boolean, isPremium: boolean, usedThisMonth?: number, limit?: number|null, remaining?: number|null, yearMonth?: string, quotaCheckUnavailable?: boolean }>}
 */
export async function fetchManualSnippetCreationQuota() {
  try {
    const fn = httpsCallable(subscriptionQuotaFunctions(), 'validateSnippetCreation');
    const result = await fn();
    return result.data;
  } catch (e) {
    return snippetCallableUnavailablePayload('validateSnippetCreation', e);
  }
}

/**
 * @returns {Promise<{ allowed: boolean, isPremium: boolean, createdThisMonth?: number, limit?: number|null, remaining?: number|null, yearMonth?: string, quotaCheckUnavailable?: boolean }>}
 */
function cookbookQuotaHttpsCallable() {
  const functions = subscriptionQuotaFunctions();
  return httpsCallable(functions, 'validateCookbookSave', { timeout: 120000 });
}

async function invokeValidateCookbookSaveOnce() {
  const call = cookbookQuotaHttpsCallable();
  const result = await call();
  const data = result?.data;
  if (data && typeof data === 'object') {
    return data;
  }
  const err = new Error('validateCookbookSave returned no data');
  err.code = 'functions/internal';
  throw err;
}

/**
 * Cloud Run occasionally fails the first invocation (cold start / quota). Retry once.
 */
export async function fetchCookbookSaveQuota() {
  const retriable = (e) =>
    e?.code === 'functions/internal' ||
    e?.code === 'internal' ||
    e?.code === 'functions/unavailable' ||
    e?.code === 'unavailable' ||
    e?.code === 'functions/deadline-exceeded' ||
    e?.code === 'deadline-exceeded';

  try {
    return await invokeValidateCookbookSaveOnce();
  } catch (e) {
    if (!retriable(e)) {
      return cookbookCallableUnavailablePayload('validateCookbookSave', e);
    }
    console.warn('[subscriptionQuota] validateCookbookSave retry after', e?.code);
    await new Promise((r) => setTimeout(r, 1200));
    try {
      return await invokeValidateCookbookSaveOnce();
    } catch (e2) {
      return cookbookCallableUnavailablePayload('validateCookbookSave', e2);
    }
  }
}
